import { useState, useCallback, useRef, useEffect } from 'react';
import { apiFetch, getToken, track } from '../dppApi';
import { useLocalLang, LANG_LABELS } from '../hooks/useLocalLang';

// Ask Bloom for the collector: a multi-turn assistant on the My Collection home.
// The thread of {role, text, link?, choices?} is held here and re-sent each turn
// to /api/user/assistant/ask. For insurance the agent narrows an ambiguous piece
// with discriminating questions, then presents the few remaining as IMAGE choices
// (tapping one resolves it instantly). See backend user_query_agent.
const SUGGESTIONS = [
    'My necklace lost a gem and needs repair',
    'Please help me with the insurance details for my bracelet',
    'What is the current value of my portfolio?',
];

const SpeechRecognition =
    typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
const speechSupported = !!SpeechRecognition;

const DICTATION_LOCALE =
    (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en-US';

const MicIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
);

// Format an ISO datetime (a datetime-local value) into a readable line.
const fmtWhen = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

// `seed` (optional): a pre-composed inquiry handed in from the passport's
// "Contact Merchant" button for a logged-in visitor — { qr_code_id, product,
// merchant }. When present, the assistant opens straight to the in-store/virtual
// choice for that product, skipping the classification round-trip.
const UserAssistant = ({ seed } = {}) => {
    const [messages, setMessages] = useState([]);   // {role:'user'|'assistant', text, link?, choices?, intent?}
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [listening, setListening] = useState(false);
    const [meetFor, setMeetFor] = useState(null);   // { i, type } — inquiry message + chosen meeting type, while picking a slot
    const [meetWhen, setMeetWhen] = useState('');   // the datetime-local value being chosen
    const [meetMsg, setMeetMsg] = useState('');     // free-text purpose of the inquiry (price, try-on, similar pieces…)
    const recognitionRef = useRef(null);
    const inquiryActiveRef = useRef(false);   // is an inquiry form (with its textarea) currently shown? routes the mic there
    const inputRef = useRef(null);
    const threadRef = useRef(null);

    // EN/local toggle — same location-based detection Passport.js uses. The
    // toggle (once shown) always pins the reply language; with no local
    // language detected, no toggle appears and the assistant keeps matching
    // the question's own language (the pre-existing behavior).
    const localLang = useLocalLang();
    const [showLocal, setShowLocal] = useState(false);
    const localLabel = LANG_LABELS[localLang] || localLang?.toUpperCase();
    const replyLang = localLang ? (showLocal ? localLang : 'en') : null;

    // Translate the suggestion chips into the local language (same batch-translate
    // endpoint/pattern Passport.js uses for its static field values) — cached per
    // language so switching back and forth doesn't re-request it.
    const [chipsT, setChipsT] = useState({}); // { [lang]: SUGGESTIONS translated, same order }
    useEffect(() => {
        if (!showLocal || !localLang || chipsT[localLang]) return;
        apiFetch('/api/passport/translate-batch', { method: 'POST', body: { lang: localLang, texts: SUGGESTIONS } })
            .then(d => {
                if (Array.isArray(d.translations) && d.translations.length === SUGGESTIONS.length) {
                    setChipsT(prev => ({ ...prev, [localLang]: d.translations }));
                }
            })
            .catch(() => {});
    }, [showLocal, localLang, chipsT]);
    const displaySuggestions = (showLocal && chipsT[localLang]) || SUGGESTIONS;

    // Grow the textarea to fit its content.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [input]);

    // Keep the thread scrolled to the latest turn.
    useEffect(() => {
        const el = threadRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, loading]);

    // While an inquiry form is on screen, the Ask Bloom mic dictates into its
    // textarea (below) instead of submitting a chat turn.
    useEffect(() => { inquiryActiveRef.current = messages.some(m => m.inquiry); }, [messages]);

    // Seeded from the passport "Contact Merchant" button: open straight to the
    // in-store/virtual choice for the product they were viewing (once).
    const seededRef = useRef(false);
    useEffect(() => {
        if (!seed || seededRef.current) return;
        seededRef.current = true;
        setMessages([{
            role: 'assistant', intent: 'inquiry',
            text: `You're inquiring about ${seed.product || 'this piece'}${seed.merchant ? ` from ${seed.merchant}` : ''}. What would you like to ask?`,
            inquiry: { qr_code_id: seed.qr_code_id, merchant: seed.merchant, message: `Inquiry about ${seed.product || 'a piece'}`, source: 'passport' },
        }]);
    }, [seed]);

    // One request: append the user's turn, POST the whole thread, append the reply.
    const send = useCallback(async ({ text, choice, docType }) => {
        if (loading) return;
        track('user', 'ask_bloom_send');
        const userTurn = { role: 'user', text: choice ? choice.label : (text || '') };
        const nextMessages = [...messages, userTurn];
        setMessages(nextMessages);
        setInput('');
        setError(null);
        setLoading(true);
        try {
            const body = { messages: nextMessages.map(m => ({ role: m.role, text: m.text })) };
            if (choice) {
                body.passport_id = choice.passport_id;   // instant resolve of a picked piece
                if (docType) body.doc_type = docType;     // which document they were after
            }
            if (replyLang) body.lang = replyLang;
            const data = await apiFetch('/api/user/assistant/ask', {
                method: 'POST', token: getToken('user'), body,
            });
            setMessages(m => [...m, {
                role: 'assistant', text: data.answer, link: data.link,
                choices: data.choices, intent: data.intent, doc_type: data.doc_type,
                maintenance: data.maintenance, inquiry: data.inquiry,
            }]);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loading, messages, replyLang]);

    const submitText = useCallback((t) => {
        const q = (t ?? input).trim();
        if (!q) return;
        send({ text: q });
    }, [input, send]);

    const pick = useCallback((choice, docType) => send({ choice, docType }), [send]);

    // User chose a fulfilment option for a maintenance request -> create it.
    const requestMaintenance = useCallback(async (maint, fulfilment) => {
        if (loading) return;
        setLoading(true);
        setError(null);
        try {
            // A piece in the collection carries passport_id; an external request
            // (product not owned) carries brand_id + item_description instead.
            const body = maint.passport_id
                ? { qr_code_id: maint.passport_id, issue: maint.issue, fulfilment, source: 'assistant' }
                : { brand_id: maint.brand_id, item_description: maint.item_description, issue: maint.issue, fulfilment, source: 'assistant' };
            await apiFetch('/api/maintenance', {
                method: 'POST', token: getToken('user'), body,
            });
            const how = fulfilment === 'pickup' ? 'a home pick-up' : 'drop-off at the nearest store';
            const who = maint.merchant ? maint.merchant : 'the merchant';
            setMessages(m => [...m, { role: 'assistant', text: `Done — your repair request is with ${who}, set for ${how}. They'll be in touch.` }]);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loading]);

    // User chose how to meet for an inquiry -> create it. The merchant then
    // proposes an appointment time (negotiated on the collection dashboard).
    const requestInquiry = useCallback(async (inq, meetingType, appointmentTime, message) => {
        if (loading) return;
        setLoading(true);
        setError(null);
        try {
            // A specific piece carries qr_code_id (the passport "Contact Merchant"
            // flow, or an owned piece the agent resolved); a shopping inquiry the
            // agent matched carries brand_id + item_description. `source` says which
            // entry point it came from. For an in-store visit the user picks a slot
            // up front (appointment_time), which the merchant approves or counters.
            // `message` is the user's typed purpose; fall back to the auto summary.
            const base = { message: (message || '').trim() || inq.message, meeting_type: meetingType };
            if (appointmentTime) base.appointment_time = appointmentTime;
            const body = inq.qr_code_id
                ? { ...base, qr_code_id: inq.qr_code_id, source: inq.source || 'assistant' }
                : { ...base, brand_id: inq.brand_id, item_description: inq.item_description, source: 'assistant' };
            await apiFetch('/api/inquiry', {
                method: 'POST', token: getToken('user'), body,
            });
            const who = inq.merchant ? inq.merchant : 'the merchant';
            const how = meetingType === 'virtual' ? 'virtual call' : 'in-store visit';
            const text = appointmentTime
                ? `Done — your ${how} with ${who} is requested for ${fmtWhen(appointmentTime)}. They'll approve it or suggest another time; track it under Your inquiries.`
                : `Done — your inquiry is with ${who} for a ${how}. They'll propose a time, which you can accept or counter under Your inquiries.`;
            setMessages(m => [...m, { role: 'assistant', text }]);
            setMeetFor(null);
            setMeetMsg('');
        } catch (err) {
            // Surface the reason in the thread (e.g. "You already have an active
            // inquiry for this piece.") rather than a detached error line.
            setMeetFor(null);
            setMessages(m => [...m, { role: 'assistant', intent: 'unsupported', text: err.message }]);
        } finally {
            setLoading(false);
        }
    }, [loading]);

    const toggleMic = useCallback(() => {
        if (!speechSupported) return;
        if (listening) { recognitionRef.current?.stop(); return; }
        const rec = new SpeechRecognition();
        rec.lang = DICTATION_LOCALE;
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e) => {
            const t = e.results[0][0].transcript;
            // With an inquiry form open, dictate into its message textarea (append);
            // otherwise treat speech as a normal chat turn.
            if (inquiryActiveRef.current) {
                setMeetMsg(prev => ((prev ? prev.trim() + ' ' : '') + t).slice(0, 500));
            } else {
                submitText(t);
            }
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => setListening(false);
        recognitionRef.current = rec;
        setListening(true);
        rec.start();
    }, [listening, submitText]);

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitText(); }
    };

    const reset = useCallback(() => {
        recognitionRef.current?.stop();
        setMessages([]);
        setInput('');
        setError(null);
        setLoading(false);
        setMeetFor(null);
        setMeetMsg('');
        inputRef.current?.focus();
    }, []);

    const started = messages.length > 0;

    return (
        <div className="dpp-card insights-card assistant-card">
            <div className="assistant-card-header">
                <div className="insights-card-title">Ask Bloom</div>
                {localLang && (
                    <div className="assistant-lang-toggle">
                        <button
                            className={`lang-btn${!showLocal ? " active" : ""}`}
                            onClick={() => setShowLocal(false)}
                        >EN</button>
                        <button
                            className={`lang-btn${showLocal ? " active" : ""}`}
                            onClick={() => setShowLocal(true)}
                        >{localLabel}</button>
                    </div>
                )}
            </div>

            {started && (
                <div className="assistant-thread" ref={threadRef}>
                    {messages.map((m, i) => (
                        <div key={i} className={`assistant-msg assistant-msg--${m.role === 'user' ? 'user' : 'bot'}${m.intent === 'unsupported' ? ' assistant-msg--muted' : ''}`}>
                            {m.text && <span className="assistant-msg-text">{m.text}</span>}
                            {m.link && (
                                <a className="assistant-doc-link" href={m.link.url} target="_blank" rel="noreferrer">
                                    {m.link.label}
                                </a>
                            )}
                            {m.choices?.length > 0 && (
                                <div className="assistant-choices">
                                    {m.choices.map(c => (
                                        <button
                                            key={c.passport_id}
                                            className="assistant-choice"
                                            onClick={() => pick(c, m.doc_type)}
                                            disabled={loading}
                                            title={c.label}
                                        >
                                            {c.image_url
                                                ? <img src={c.image_url} alt={c.label} />
                                                : <span className="assistant-choice-noimg">No image</span>}
                                            <span className="assistant-choice-label">{c.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {m.maintenance && (
                                <div className="assistant-fulfilment">
                                    <button className="dpp-button secondary" disabled={loading} onClick={() => requestMaintenance(m.maintenance, 'pickup')}>Pick up at home</button>
                                    <button className="dpp-button secondary" disabled={loading} onClick={() => requestMaintenance(m.maintenance, 'dropoff')}>Drop off at nearest store</button>
                                </div>
                            )}
                            {m.inquiry && (
                                <div className="assistant-inquiry-form">
                                    <textarea
                                        className="dpp-input assistant-inquiry-msg"
                                        placeholder="Price, a try-on, similar items, availability of limited edition pieces, etc."
                                        maxLength={500}
                                        value={meetMsg}
                                        disabled={loading}
                                        onChange={e => setMeetMsg(e.target.value)}
                                    />
                                    <div className="assistant-meet-label">How would you like to meet?</div>
                                    <div className="assistant-fulfilment">
                                        <button className={`dpp-button secondary${meetFor?.i === i && meetFor.type === 'in_store' ? ' is-active' : ''}`} disabled={loading} onClick={() => { setMeetWhen(''); setMeetFor({ i, type: 'in_store' }); }}>In-store visit</button>
                                        <button className={`dpp-button secondary${meetFor?.i === i && meetFor.type === 'virtual' ? ' is-active' : ''}`} disabled={loading} onClick={() => { setMeetWhen(''); setMeetFor({ i, type: 'virtual' }); }}>Virtual call</button>
                                    </div>
                                    {meetFor?.i === i && (
                                        <div className="assistant-fulfilment">
                                            <input
                                                className="dpp-input assistant-datetime"
                                                type="datetime-local"
                                                value={meetWhen}
                                                disabled={loading}
                                                onChange={e => setMeetWhen(e.target.value)}
                                            />
                                            <button className="dpp-button secondary" disabled={loading || !meetWhen} onClick={() => requestInquiry(m.inquiry, meetFor.type, meetWhen, meetMsg)}>
                                                {meetFor.type === 'virtual' ? 'Request call' : 'Request visit'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    {loading && (
                        <div className="assistant-msg assistant-msg--bot">
                            <span className="assistant-msg-text">Thinking…</span>
                        </div>
                    )}
                </div>
            )}

            <div className="assistant-input-row">
                <button
                    type="button"
                    className={`assistant-mic${listening ? ' is-listening' : ''}`}
                    onClick={toggleMic}
                    disabled={!speechSupported}
                    title={speechSupported ? (listening ? 'Stop' : 'Speak your question') : 'Voice input not supported'}
                    aria-label={listening ? 'Stop listening' : 'Speak your question'}
                >
                    {MicIcon}
                </button>
                <textarea
                    ref={inputRef}
                    className="dpp-input assistant-input"
                    rows={1}
                    placeholder=""
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                />
                <button className="dpp-button" onClick={() => submitText()} disabled={loading || !input.trim()}>
                    {loading ? '…' : 'Ask'}
                </button>
            </div>

            {listening && (
                <div className="assistant-listening-status" role="status" aria-live="polite">
                    <span className="assistant-listening-dot" aria-hidden="true" />
                    Listening…
                </div>
            )}

            {!started && !loading && (
                <div className="assistant-suggestions">
                    {displaySuggestions.map((label, i) => (
                        <button key={SUGGESTIONS[i]} className="assistant-chip" onClick={() => submitText(label)}>
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {error && <p className="dpp-error">{error}</p>}

            {started && (
                <div className="assistant-clear-row">
                    <button type="button" className="assistant-clear" onClick={reset}>
                        Clear
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserAssistant;
